// Pop-up met de bevestigingsvragen van blok C deel 2:
//  - extra werken uit offertes die na de opstartfiche verkocht werden (SPEC_workflow 4);
//  - posten herkend in het veld extra informatie (SPEC_calculatie 8, 9);
//  - werkposten met een andere eenheid dan de prijslijst (SPEC_calculatie 8.1).
// Een post telt pas mee na bevestiging. Enkel Eranda, Timen, Ervin of Jordy kunnen bevestigen (SPEC_workflow 10);
// elke bevestiging wordt een regel voor volgende calculaties (SPEC_calculatie 10).
import { useState } from 'react';
import {
  bevestigEenheid,
  postUitExtraInfo,
  type ExtraInfoVraag,
  type ExtraOfferteBeslissing,
  type MatchedLine,
  type MeerwerkLine,
} from '@recotex/shared';
import { fmtDate, fmtEUR, fmtNum } from '../lib/format';

export interface VragenStaat {
  matchedLines: MatchedLine[];
  meerwerkLines: MeerwerkLine[];
  extraOffertes: ExtraOfferteBeslissing[];
  extraInfoVragen: ExtraInfoVraag[];
  /** Fragmenten die geen betaalbare post zijn. */
  genegeerd: string[];
}

export function telVragen(s: Pick<VragenStaat, 'matchedLines' | 'meerwerkLines' | 'extraInfoVragen'>) {
  const offertes = new Set(s.meerwerkLines.filter((m) => m.extraOfferte?.status === 'open').map((m) => m.extraOfferte!.offerteId)).size;
  const info = s.extraInfoVragen.length;
  const eenheden = s.matchedLines.filter((l) => l.eenheidVraag?.status === 'open').length;
  return { offertes, info, eenheden, totaal: offertes + info + eenheden };
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Kaartje op de pagina zelf, zodat de vragen niet verloren gaan als de pop-up dicht is. */
export function BevestigVragenKaart({ staat, onOpen }: { staat: Pick<VragenStaat, 'matchedLines' | 'meerwerkLines' | 'extraInfoVragen'>; onOpen: () => void }) {
  const t = telVragen(staat);
  if (!t.totaal) return null;
  const delen = [
    t.offertes ? `${t.offertes} extra offerte${t.offertes === 1 ? '' : 's'} na de opstartfiche` : '',
    t.info ? `${t.info} post${t.info === 1 ? '' : 'en'} uit extra informatie` : '',
    t.eenheden ? `${t.eenheden} post${t.eenheden === 1 ? '' : 'en'} met een andere eenheid` : '',
  ].filter(Boolean);
  return (
    <div className="card mb-5 border-l-4 border-warning normal-case tracking-normal flex items-center gap-3 flex-wrap" role="status">
      <div className="flex-1 min-w-[240px]">
        <h3 className="text-lg font-bold">Bevestiging nodig ({t.totaal})</h3>
        <p className="text-sm text-secondary">{delen.join(' · ')}. Ze tellen pas mee na bevestiging.</p>
      </div>
      <button type="button" className="btn-primary btn-sm" onClick={onOpen}>Vragen bekijken</button>
    </div>
  );
}

export function BevestigVragenModal({ staat, magBevestigen, bezig, onToepassen, onSluit }: {
  staat: VragenStaat;
  magBevestigen: boolean;
  bezig?: boolean;
  onToepassen: (nieuw: VragenStaat) => void;
  onSluit: () => void;
}) {
  const [w, setW] = useState<VragenStaat>(staat);
  const [hv, setHv] = useState<Record<string, string>>({});
  const [prijs, setPrijs] = useState<Record<string, string>>({});
  const [gedaan, setGedaan] = useState(0);
  const nu = new Date().toISOString();

  const offerteGroepen = [...new Map(staat.meerwerkLines.filter((m) => m.extraOfferte?.status === 'open').map((m) => [m.extraOfferte!.offerteId, m.extraOfferte!] as const)).values()];
  const eenheidLijnen = staat.matchedLines.filter((l) => l.eenheidVraag?.status === 'open');
  const beslistOfferte = (id: number) => w.extraOffertes.find((o) => o.offerteId === id)?.beslissing ?? (w.meerwerkLines.some((m) => m.extraOfferte?.offerteId === id && m.extraOfferte.status === 'open') ? 'open' : 'bevestigd');
  const getal = (sleutel: string, standaard: number | null) => {
    const ruw = hv[sleutel];
    const n = ruw == null || ruw === '' ? standaard : parseFloat(ruw.replace(',', '.'));
    return n != null && Number.isFinite(n) && n > 0 ? n : null;
  };

  const beslisOfferte = (offerteId: number, ja: boolean) => {
    setW((s) => {
      const meerwerkLines = ja
        ? s.meerwerkLines.map((m) => {
            if (m.extraOfferte?.offerteId !== offerteId) return m;
            const p = prijs[m.id] != null && prijs[m.id] !== '' ? parseFloat(prijs[m.id]!.replace(',', '.')) : m.prijs;
            const pp = Number.isFinite(p) ? p : m.prijs;
            return { ...m, prijs: pp, subtotaal: r2(pp * m.hoeveelheid), extraOfferte: { ...m.extraOfferte, status: 'bevestigd' as const } };
          })
        : s.meerwerkLines.filter((m) => m.extraOfferte?.offerteId !== offerteId);
      const bekend = s.extraOffertes.some((o) => o.offerteId === offerteId);
      const bron = staat.meerwerkLines.find((m) => m.extraOfferte?.offerteId === offerteId)?.extraOfferte;
      const extraOffertes = bekend
        ? s.extraOffertes.map((o) => (o.offerteId === offerteId ? { ...o, beslissing: ja ? ('bevestigd' as const) : ('geweigerd' as const) } : o))
        : [...s.extraOffertes, { offerteId, nummer: bron?.nummer ?? String(offerteId), datum: bron?.datum ?? null, beslissing: ja ? ('bevestigd' as const) : ('geweigerd' as const) }];
      return { ...s, meerwerkLines, extraOffertes };
    });
    setGedaan((n) => n + 1);
  };

  const beslisInfo = (v: ExtraInfoVraag, ja: boolean) => {
    setW((s) => {
      const extraInfoVragen = s.extraInfoVragen.filter((x) => x.id !== v.id);
      if (!ja) return { ...s, extraInfoVragen, genegeerd: [...s.genegeerd, v.fragment] };
      const n = getal(v.id, v.hoeveelheid);
      if (n == null) return s;
      const r = postUitExtraInfo(v, n, { op: nu });
      return {
        ...s,
        extraInfoVragen,
        matchedLines: r.werkpost ? [...s.matchedLines, r.werkpost] : s.matchedLines,
        meerwerkLines: r.meerwerk ? [...s.meerwerkLines, r.meerwerk] : s.meerwerkLines,
      };
    });
    setGedaan((n) => n + 1);
  };

  const beslisEenheid = (l: MatchedLine, ja: boolean) => {
    setW((s) => {
      if (!ja) return { ...s, matchedLines: s.matchedLines.filter((x) => x.id !== l.id) };
      const n = getal(l.id, l.eenheidVraag!.ficheHoeveelheid);
      if (n == null) return s;
      return { ...s, matchedLines: s.matchedLines.map((x) => (x.id === l.id ? bevestigEenheid(x, n) : x)) };
    });
    setGedaan((n) => n + 1);
  };

  const infoOpen = (v: ExtraInfoVraag) => w.extraInfoVragen.some((x) => x.id === v.id);
  const eenheidOpen = (l: MatchedLine) => w.matchedLines.find((x) => x.id === l.id)?.eenheidVraag?.status === 'open';
  const knopKlein = 'btn-ghost btn-sm';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="bevestig-titel">
      <div className="card max-w-3xl w-full max-h-full overflow-y-auto normal-case tracking-normal">
        <h2 id="bevestig-titel" className="text-xl font-bold mb-1">Graag bevestigen</h2>
        <p className="text-sm text-secondary mb-4">
          Deze posten staan nog niet in de calculatie. Ze tellen pas mee nadat je ze bevestigt; je antwoord wordt onthouden voor volgende calculaties.
          {!magBevestigen && <strong className="block mt-1 text-primary">Enkel Eranda, Timen, Ervin of Jordy kunnen deze vragen beantwoorden.</strong>}
        </p>

        {offerteGroepen.length > 0 && (
          <section className="mb-5">
            <h3 className="text-base font-bold mb-1">Extra werken na de opstartfiche</h3>
            <p className="text-sm mb-2 p-2 rounded-md bg-warning-100 border-l-4 border-warning">
              <strong>Opgelet,</strong> dit zijn extra werken die na de opstartfiche verkocht zijn. Graag bevestigen: bevestigd blijven ze in de calculatie en de PDF, anders gaan ze eruit.
            </p>
            <div className="space-y-3">
              {offerteGroepen.map((o) => {
                const lijnen = staat.meerwerkLines.filter((m) => m.extraOfferte?.offerteId === o.offerteId);
                const besl = beslistOfferte(o.offerteId);
                const som = r2(lijnen.reduce((s, m) => s + (prijs[m.id] ? parseFloat(prijs[m.id]!.replace(',', '.')) || 0 : m.prijs) * m.hoeveelheid, 0));
                return (
                  <div key={o.offerteId} className={`rounded-md border p-3 ${besl === 'open' ? 'border-warning' : 'border-border bg-lightgrey'}`}>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                      <div className="font-semibold">
                        Offerte {o.nummer}{o.datum ? <span className="text-secondary font-normal"> van {fmtDate(o.datum)}</span> : null}
                        {o.projectCode ? <span className="text-secondary font-normal"> · {o.projectCode}</span> : null}
                      </div>
                      <div className="text-sm">Voor de onderaannemer <strong className="tabular-nums whitespace-nowrap">{fmtEUR(som)}</strong></div>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {lijnen.map((m) => (
                          <tr key={m.id} className="border-b border-lightgrey last:border-b-0">
                            <td className="py-1 pr-2">{m.pdfNaam}{m.naamArbeid && m.naamArbeid !== 'MEERWERK' ? <span className="text-xs text-secondary block">{m.naamArbeid}</span> : <span className="text-xs text-warning block">niet gekoppeld aan de prijslijst: vul de prijs in</span>}</td>
                            <td className="py-1 px-2 text-right tabular-nums whitespace-nowrap">{fmtNum(m.hoeveelheid)} {m.eenheid}</td>
                            <td className="py-1 px-2 text-right w-28">
                              {besl === 'open' && magBevestigen ? (
                                <input
                                  className="input text-right py-1 w-24 tabular-nums"
                                  inputMode="decimal"
                                  aria-label={`Prijs voor ${m.pdfNaam}`}
                                  value={prijs[m.id] ?? String(m.prijs)}
                                  onChange={(e) => setPrijs((p) => ({ ...p, [m.id]: e.target.value }))}
                                />
                              ) : <span className="tabular-nums">{fmtEUR(m.prijs)}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-2 flex gap-2 items-center flex-wrap">
                      {besl === 'open' ? (
                        magBevestigen ? (
                          <>
                            <button type="button" className="btn-primary btn-sm" onClick={() => beslisOfferte(o.offerteId, true)}>Bevestigen</button>
                            <button type="button" className={knopKlein} onClick={() => beslisOfferte(o.offerteId, false)}>Niet opnemen</button>
                          </>
                        ) : <span className="text-xs text-secondary">Wacht op bevestiging.</span>
                      ) : (
                        <span className={besl === 'bevestigd' ? 'chip-success' : 'chip-neutral'}>{besl === 'bevestigd' ? 'bevestigd, telt mee' : 'niet opgenomen'}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {staat.extraInfoVragen.length > 0 && (
          <section className="mb-5">
            <h3 className="text-base font-bold mb-1">Herkend in extra informatie</h3>
            <p className="text-xs text-secondary mb-2">De eenheidsprijs komt uit de prijslijst. Pas de hoeveelheid aan als ze niet klopt.</p>
            <div className="space-y-2">
              {staat.extraInfoVragen.map((v) => {
                const open = infoOpen(v);
                const n = getal(v.id, v.hoeveelheid);
                return (
                  <div key={v.id} className={`rounded-md border p-3 ${open ? 'border-warning' : 'border-border bg-lightgrey'}`}>
                    <p className="text-sm"><span className="text-secondary">Op de fiche:</span> “{v.fragment}”</p>
                    <p className="text-sm font-semibold mt-1">{v.uitleg}</p>
                    <div className="mt-2 flex gap-2 items-center flex-wrap">
                      {open ? (
                        magBevestigen ? (
                          <>
                            <label className="text-sm flex items-center gap-1">
                              <input
                                className="input text-right py-1 w-20 tabular-nums"
                                inputMode="decimal"
                                aria-label={`Hoeveelheid voor ${v.post?.naam ?? v.fragment}`}
                                placeholder="?"
                                value={hv[v.id] ?? (v.hoeveelheid != null ? String(v.hoeveelheid) : '')}
                                onChange={(e) => setHv((h) => ({ ...h, [v.id]: e.target.value }))}
                              />
                              {v.eenheid}
                            </label>
                            <button type="button" className="btn-primary btn-sm" disabled={n == null} onClick={() => beslisInfo(v, true)}>Toevoegen</button>
                            <button type="button" className={knopKlein} onClick={() => beslisInfo(v, false)}>Geen betaalbare post</button>
                          </>
                        ) : <span className="text-xs text-secondary">Wacht op bevestiging.</span>
                      ) : (
                        <span className={w.genegeerd.includes(v.fragment) ? 'chip-neutral' : 'chip-success'}>{w.genegeerd.includes(v.fragment) ? 'genegeerd' : 'toegevoegd'}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {eenheidLijnen.length > 0 && (
          <section className="mb-5">
            <h3 className="text-base font-bold mb-1">Andere eenheid dan de prijslijst</h3>
            <div className="space-y-2">
              {eenheidLijnen.map((l) => {
                const v = l.eenheidVraag!;
                const open = eenheidOpen(l);
                const n = getal(l.id, v.ficheHoeveelheid);
                return (
                  <div key={l.id} className={`rounded-md border p-3 ${open ? 'border-warning' : 'border-border bg-lightgrey'}`}>
                    <p className="text-sm">
                      Op de fiche staat <strong>{fmtNum(v.ficheHoeveelheid)} {v.ficheEenheid}</strong> {l.subItems[0]?.pdfNaam ?? l.naamArbeid}, maar de prijslijst rekent
                      {(l.subItems[0]?.pdfNaam ?? '').trim().toUpperCase() === l.naamArbeid.trim().toUpperCase() ? '' : <> <strong>{l.naamArbeid}</strong></>}
                      {' '}per {v.prijslijstEenheid} aan {l.prijs != null ? fmtEUR(l.prijs) : '—'}. Is dat {fmtNum(v.ficheHoeveelheid)} {v.prijslijstEenheid}?
                    </p>
                    <div className="mt-2 flex gap-2 items-center flex-wrap">
                      {open ? (
                        magBevestigen ? (
                          <>
                            <label className="text-sm flex items-center gap-1">
                              <input
                                className="input text-right py-1 w-20 tabular-nums"
                                inputMode="decimal"
                                aria-label={`Hoeveelheid in ${v.prijslijstEenheid}`}
                                value={hv[l.id] ?? String(v.ficheHoeveelheid)}
                                onChange={(e) => setHv((h) => ({ ...h, [l.id]: e.target.value }))}
                              />
                              {v.prijslijstEenheid}
                            </label>
                            <button type="button" className="btn-primary btn-sm" disabled={n == null} onClick={() => beslisEenheid(l, true)}>Bevestigen</button>
                            <button type="button" className={knopKlein} onClick={() => beslisEenheid(l, false)}>Post weglaten</button>
                          </>
                        ) : <span className="text-xs text-secondary">Wacht op bevestiging.</span>
                      ) : (
                        <span className={w.matchedLines.some((x) => x.id === l.id) ? 'chip-success' : 'chip-neutral'}>{w.matchedLines.some((x) => x.id === l.id) ? 'bevestigd, telt mee' : 'weggelaten'}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="flex justify-end gap-2 mt-2 sticky bottom-0 bg-white pt-3 border-t border-border">
          <button type="button" className="btn-ghost" onClick={onSluit} disabled={bezig}>Later</button>
          {magBevestigen && (
            <button type="button" className="btn-primary" disabled={bezig || gedaan === 0} onClick={() => onToepassen(w)}>
              {bezig ? 'Opslaan…' : 'Antwoorden opslaan'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
